import { makeDoorOperationInfo } from "building-generation/elements/doors/doorOperationInfo";
import { doorIntersectsCutPlane } from "building-generation/elements/doors/doorQuery";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { SheetGeometryType, sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { doorPlacementsOnLevel } from "doc/building/doorsOnLevel";
import type { PlanCollider } from "doc/plans/interiorLabelObstacle";
import { CollisionType, planColliderObstacleNew } from "doc/plans/interiorLabelObstacle";
import type { Obstacles, PlanGenerationContext, PlanGenerationContextBase } from "doc/plans/planGeometrySource";
import type { TaggedPlanGeometry } from "doc/plans/planGeometrySourceTypes";
import { makePlanColliderFromSheetGeometry } from "doc/plans/sheetGeometryCollision";
import { isDoorSwing } from "doc/utils/doorTools";
import type { Label } from "lib/house/plans/label";
import { labelNew, tagFontSize } from "lib/house/plans/label";
import { getReportingUnitRoomIds, hostRoomsInReportingUnit } from "../reportingUnitRooms";
import { makeSheetGeometryFromLabels } from "./planLabels";
import { makeSheetGeometryForOpeningCutTrim } from "./planOpenings";
import { placeOpeningTags } from "./planTags";

type DoorPlanContext = PlanGenerationContextBase & Pick<PlanGenerationContext, "selectedReportingUnitLabelId">;

function doorsOnLevelInReportingUnit(
  building: PlanGenerationContext["building"],
  levelId: string,
  unitLabelId?: string,
  cutPlaneHeight?: LevelInches,
) {
  // Doors that don't reach the cut plane, e.g. a low cased opening, leave the wall solid where
  // they are, so the plan must not draw them either.
  const doors = doorPlacementsOnLevel(levelId, building.doorPlacements).filter((door) =>
    doorIntersectsCutPlane(door, cutPlaneHeight, building.roomFloorAssemblies),
  );
  const reportingUnitRoomIds = getReportingUnitRoomIds(building, unitLabelId);
  if (!reportingUnitRoomIds) {
    return doors;
  }
  return doors.filter((door) => hostRoomsInReportingUnit(reportingUnitRoomIds, [door.frontRoom, door.backRoom]));
}

export function makeSheetGeometryForDoorTrim({
  building,
  levelId,
  cutPlaneHeight,
  selectedReportingUnitLabelId,
}: DoorPlanContext) {
  const result: SheetGeometry[] = [];
  const doors = doorsOnLevelInReportingUnit(building, levelId, selectedReportingUnitLabelId, cutPlaneHeight);

  for (const door of doors) {
    const doorHandle = door.handle ?? door.frontRoom.roomHandle;
    if (doorHandle && door.opening) {
      for (const geometry of makeSheetGeometryForOpeningCutTrim(doorHandle, door.opening)) {
        result.push({ ...geometry, buildingEntity: door });
      }
    }
  }

  return result;
}

export function makeSheetGeometryForDoors({
  building,
  levelId,
  cutPlaneHeight,
  selectedReportingUnitLabelId,
}: DoorPlanContext) {
  const doors = doorsOnLevelInReportingUnit(building, levelId, selectedReportingUnitLabelId, cutPlaneHeight);
  const result: SheetGeometry[] = [];

  for (const door of doors) {
    result.push(...makeSheetGeometryForDoor(door));
  }

  return result;
}

export function makeSheetGeometryForDoorsOperationLinework({
  building,
  levelId,
  cutPlaneHeight,
  selectedReportingUnitLabelId,
}: DoorPlanContext) {
  const doors = doorsOnLevelInReportingUnit(building, levelId, selectedReportingUnitLabelId, cutPlaneHeight);
  const result: SheetGeometry[] = [];

  for (const door of doors) {
    result.push(...makeSheetGeometryForDoorOperationLinework(door, building.settings.planset.includeSliderOperation));
  }

  return result;
}

export function makeSheetGeometryForDoor(door: GeneratedDoorPlacement) {
  const doorHandle = door.handle ?? door.frontRoom.roomHandle;
  const doorOperationInfo = makeDoorOperationInfo(door);

  const result: SheetGeometry[] = [];
  if (!doorOperationInfo) {
    return result;
  }

  const { doorBodyLinework = [], doorBodyFixedLinework = [], doorBodyOBBs = [] } = doorOperationInfo;
  const doorLinework = [...doorBodyLinework, ...doorBodyFixedLinework];

  return [
    sheetPolylineGeometryNew({
      paths: doorLinework,
      handle: doorHandle,
      buildingEntity: door,
      // pocket doors are rendered above the walls, so they need to be filled
      // negating this property invalidates the below properties
      fill: door.descriptor.operationType === "POCKET",
      oneObstacle: true,
      relaxationObstacleOBBs: doorBodyOBBs,
    }),
  ];
}

export function makeSheetGeometryForDoorOperationLinework(
  door: GeneratedDoorPlacement,
  includeSliderOperationArrowLinework?: boolean,
) {
  const doorHandle = door.handle ?? door.frontRoom.roomHandle;
  const doorOperationInfo = makeDoorOperationInfo(door);

  const result: SheetGeometry[] = [];
  if (!doorOperationInfo) {
    return result;
  }

  const {
    planOutlineInteriorLinework = [],
    planOutlineExteriorLinework = [],
    sliderOperationArrowLinework = [],
    doorSwingLinework = [],
    overheadOperationLinework = [],
    operationLinework = [],
    doorSwingLineworkOBBs = [],
  } = doorOperationInfo;

  const dashedOperationLinework = [...planOutlineInteriorLinework, ...overheadOperationLinework, ...operationLinework];
  const solidOperationLinework = [...doorSwingLinework, ...planOutlineExteriorLinework];
  // TODO - can we remove this check?
  if (includeSliderOperationArrowLinework) {
    solidOperationLinework.push(...sliderOperationArrowLinework);
  }

  // add order will reflect render order
  // we need solid lines rendered above dashed lines
  return [
    sheetPolylineGeometryNew({
      handle: doorHandle,
      buildingEntity: door,
      paths: dashedOperationLinework,
      dash: true,
      oneObstacle: true,
      relaxationObstacleOBBs: doorSwingLineworkOBBs,
    }),
    sheetPolylineGeometryNew({
      handle: doorHandle,
      buildingEntity: door,
      paths: solidOperationLinework,
      oneObstacle: true,
      relaxationObstacleOBBs: doorSwingLineworkOBBs,
    }),
  ];
}

export function makeSheetGeometryFromDoorTagLabels({
  building,
  levelId,
  cutPlaneHeight,
  noSettings,
  selectedReportingUnitLabelId,
}: PlanGenerationContext) {
  const doors = doorsOnLevelInReportingUnit(building, levelId, selectedReportingUnitLabelId, cutPlaneHeight);

  const { planset } = building.settings;
  const { paperSize } = planset;
  const fontSize = tagFontSize(paperSize);

  const doorTags: SheetGeometry[] = [];

  for (const door of doors) {
    const { opening, descriptor, reportedName: tagOverride } = door;
    const swing = isDoorSwing(descriptor.id);

    if (!swing || !opening) {
      continue;
    }

    const doorShortName = tagOverride ?? descriptor.reportedName;
    const tagDetailLabel = door.tagDetails ?? door.descriptor.tagDetails;

    const doorLabels: Label[] = [];

    placeOpeningTags(
      opening,
      doorShortName,
      tagDetailLabel,
      planset,
      door,
      (text, centerX, centerY, nX, nY, textRotationRads) => {
        doorLabels.push(
          labelNew({
            text,
            fontSize,
            centerX,
            centerY,
            textRotationRads,
            relaxationNormalX: nX,
            relaxationNormalY: nY,
          }),
        );
      },
    );

    doorTags.push(
      ...makeSheetGeometryFromLabels({
        labels: doorLabels,
        handle: door.handle ?? building.levels[levelId].handle,
        noSettings,
      }),
    );
  }

  return doorTags;
}

export function collectDoorObstacles(o: Obstacles, geom: SheetGeometry) {
  if (geom.type === SheetGeometryType.POLYLINE) {
    geom.paths.forEach((pl) => o.obstacles?.push(planColliderObstacleNew(CollisionType.DoorSwingLineWork, pl, 0.25)));
  }
}

export function doorTagPlanColliders(geometry: readonly TaggedPlanGeometry[]): PlanCollider[] {
  return geometry.reduce((acc, g) => {
    if (g.type === SheetGeometryType.TEXT) {
      acc.push(
        makePlanColliderFromSheetGeometry({
          collisionType: CollisionType.OpeningTag,
          sheetTextGeomSource: g,
        }),
      );
    }
    return acc;
  }, [] as PlanCollider[]);
}
